import type { ReactNode } from "react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import type { GridColDef } from "@mui/x-data-grid"

import { getEventsTableRows } from "helpers/utils/alerts"
import type { Paged } from "types/common.types"
import type { AlertsRes } from "types/alerts.types"
import IconTextStack from "widgets/common/IconTextStack"
import DataGridTable from "widgets/common/DataGridTable"
import Spinner from "widgets/common/Spinner"

type EventsTableRow = ReturnType<typeof getEventsTableRows>[number]

// The MIT grid pages at 100 rows at most; below that the footer has nothing to offer.
const PAGE_SIZE = 100

interface EventsTableProps {
  events: Paged<AlertsRes> | undefined
  isLoading: boolean
}

function EventsTable({ events, isLoading }: EventsTableProps) {
  const { t } = useTranslation()

  const columns: GridColDef<EventsTableRow>[] = useMemo(
    () => [
      {
        field: "eventDescription",
        headerName: t("alerts.EVENT_DESCRIPTION"),
        flex: 1,
        minWidth: 250,
        valueGetter: (value: { icon: ReactNode; description: string }) =>
          value.description,
        renderCell: ({ row }) => (
          <IconTextStack
            icon={row.eventDescription.icon}
            text={row.eventDescription.description}
            noWrap
          />
        ),
      },
      {
        field: "timestamp",
        headerName: t("generic.TIMESTAMP"),
        flex: 1,
        minWidth: 150,
      },
    ],
    [t],
  )

  const rows = useMemo(() => {
    if (events) {
      return getEventsTableRows(events.results, t)
    }
  }, [events, t])

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {rows && (
            <DataGridTable
              rows={rows}
              columns={columns}
              pageSize={PAGE_SIZE}
              hideToolbar
              hideFooter={rows.length <= PAGE_SIZE}
              // On the accordion's background, closed by a line under the last row.
              sx={(theme) => ({
                backgroundColor: "transparent",
                "&&": { "--DataGrid-t-header-background-base": "transparent" },
                "& .MuiDataGrid-row--lastVisible .MuiDataGrid-cell": {
                  borderBottom: `1px solid ${theme.palette.neutral[300]}`,
                },
              })}
            />
          )}
        </>
      )}
    </>
  )
}

export default EventsTable
